import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Mail, Calendar, Shield, LogOut, Clock } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import ConfirmDialog from "../components/ui/ConfirmDialog";

export default function Profile() {
  const navigate = useNavigate();
  const { admin, signOut } = useAdminAuth();
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Sign out failed:", error);
      setIsSigningOut(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    // Append 'Z' if not present to indicate UTC
    const utcString = dateString.endsWith("Z") ? dateString : dateString + "Z";
    const date = new Date(utcString);
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    const timeOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    const datePart = date.toLocaleDateString("en-US", options);
    const timePart = date.toLocaleTimeString("en-US", timeOptions);
    return `${datePart} at ${timePart}`;
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary">My Profile</h1>
        <p className="text-text-secondary mt-1">
          View your account details and manage your session
        </p>
      </div>

      {/* Profile Card */}
      <div className="card p-8 mb-6">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          {/* Avatar */}
          <div className="flex-shrink-0">
            {admin?.picture ? (
              <img
                src={admin.picture}
                alt={admin.name}
                className="w-24 h-24 rounded-full border-4 border-primary/20"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <span className="text-3xl font-bold text-white">
                  {admin?.name?.charAt(0) || "A"}
                </span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl font-bold text-text-primary mb-1">
              {admin?.name || "Admin User"}
            </h2>
            <p className="text-text-secondary mb-4">{admin?.email}</p>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
              <Shield className="w-4 h-4" />
              Administrator
            </div>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Account Details */}
        <div className="card p-6">
          <h3 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Account Details
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-text-muted">Full Name</label>
              <p className="text-text-primary font-medium">
                {admin?.name || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm text-text-muted flex items-center gap-1">
                <Mail className="w-3 h-3" />
                Email Address
              </label>
              <p className="text-text-primary font-medium">
                {admin?.email || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm text-text-muted">Admin ID</label>
              <p className="text-text-primary font-mono text-sm">
                {admin?.id || "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Session Info */}
        <div className="card p-6">
          <h3 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Session Info
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-text-muted flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Last Login
              </label>
              <p className="text-text-primary font-medium">
                {formatDate(admin?.lastLogin)}
              </p>
            </div>
            <div>
              <label className="text-sm text-text-muted">
                Authentication Provider
              </label>
              <div className="flex items-center gap-2 mt-1">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="text-text-primary font-medium">
                  Google OAuth
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm text-text-muted">Status</label>
              <div className="flex items-center gap-2 mt-1">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-green-600 font-medium">
                  Active Session
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sign Out Section */}
      <div className="card p-6 border-red-200 bg-red-50/50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-text-primary mb-1">Sign Out</h3>
            <p className="text-sm text-text-secondary">
              End your current session and return to the login page
            </p>
          </div>
          <button
            onClick={() => setShowSignOutDialog(true)}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Sign Out Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSignOutDialog}
        onClose={() => setShowSignOutDialog(false)}
        onConfirm={handleSignOut}
        title="Sign Out"
        message="Are you sure you want to sign out? You will need to sign in again to access the admin panel."
        confirmText="Sign Out"
        cancelText="Cancel"
        variant="danger"
        isLoading={isSigningOut}
      />
    </div>
  );
}
